import * as React from 'react'
import { Plus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { FieldError } from '@/components/ui/field-error'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { BillingAccount, BillingAccountInput } from '@/types/billing'

type Errors = Partial<Record<'name' | 'included' | 'fee_fixed' | 'fee_percent', string>>
type PlanRow = { plan: string; credits: string }

/** "0,50" or "0.5" → 50. Null when it is not an amount with at most `decimals` decimals. */
function toHundredths(text: string, decimals = 2): number | null {
  const normalized = text.trim().replace(',', '.')
  if (!new RegExp(`^\\d+(\\.\\d{1,${decimals}})?$`).test(normalized)) return null
  return Math.round(Number(normalized) * 100)
}

function fromHundredths(value: number, locale: string): string {
  return (value / 100).toLocaleString(locale, { maximumFractionDigits: 2, useGrouping: false })
}

/** One form to create and to edit a billing account: only the initial values
 *  and the verb change. Amounts are typed as people say them -euros and
 *  percent- and stored as the integers the bank keeps. */
export function AccountForm({
  open, onOpenChange, account, onSubmit, saving,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  account: BillingAccount | null
  onSubmit: (input: BillingAccountInput) => Promise<void>
  saving: boolean
}) {
  const { t, i18n } = useTranslation()
  const [name, setName] = React.useState('')
  const [plans, setPlans] = React.useState<PlanRow[]>([])
  const [feeFixed, setFeeFixed] = React.useState('0')
  const [feePercent, setFeePercent] = React.useState('0')
  const [errors, setErrors] = React.useState<Errors>({})
  const nameRef = React.useRef<HTMLInputElement>(null)
  const currency = account?.packs[0]?.currency ?? 'EUR'

  React.useEffect(() => {
    if (!open) return
    setName(account?.name ?? '')
    const current = Object.entries(account?.included_by_plan ?? {})
      .map(([plan, credits]) => ({ plan, credits: String(credits) }))
    setPlans(current.length ? current : [{ plan: '', credits: '' }])
    setFeeFixed(fromHundredths(account?.pack_fee_units ?? 0, i18n.language))
    setFeePercent(fromHundredths(account?.pack_fee_bps ?? 0, i18n.language))
    setErrors({})
  }, [open, account, i18n.language])

  const setPlan = (index: number, change: Partial<PlanRow>) =>
    setPlans((rows) => rows.map((row, i) => (i === index ? { ...row, ...change } : row)))

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    const next: Errors = {}
    const filled = plans.filter((row) => row.plan.trim() || row.credits.trim())
    const names = filled.map((row) => row.plan.trim())
    if (!name.trim()) next.name = t('billing.account.errors.name')
    if (filled.some((row) => !/^[\w-]+$/.test(row.plan.trim()) || !/^\d+$/.test(row.credits.trim()))) {
      next.included = t('billing.account.errors.included')
    } else if (new Set(names).size !== names.length) {
      next.included = t('billing.account.errors.included_repeated')
    }
    const fixed = toHundredths(feeFixed)
    const percent = toHundredths(feePercent)
    if (fixed === null) next.fee_fixed = t('billing.account.errors.amount')
    if (percent === null || percent > 10000) next.fee_percent = t('billing.account.errors.percent')
    setErrors(next)
    if (Object.keys(next).length > 0) {
      if (next.name) nameRef.current?.focus()
      return
    }
    await onSubmit({
      name: name.trim(),
      included_by_plan: Object.fromEntries(filled.map((row) => [row.plan.trim(), Number(row.credits)])),
      pack_fee_units: fixed ?? 0,
      pack_fee_bps: percent ?? 0,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={(event) => void submit(event)} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{account ? t('billing.account.edit_title') : t('billing.account.create_title')}</DialogTitle>
            <DialogDescription>{t('billing.account.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="billing-name">{t('billing.account.name')}</Label>
            <Input id="billing-name" aria-label={t('billing.account.name')} ref={nameRef} value={name} onChange={(e) => setName(e.target.value)}
              aria-invalid={!!errors.name} aria-describedby={errors.name ? 'billing-name-error' : undefined} />
            <FieldError id="billing-name-error">{errors.name}</FieldError>
          </div>
          <fieldset className="space-y-2" aria-describedby={errors.included ? 'billing-included-error' : 'billing-included-help'}>
            <legend className="text-sm font-medium">{t('billing.account.included')}</legend>
            <p id="billing-included-help" className="text-xs text-muted-foreground">{t('billing.account.included_help')}</p>
            {plans.map((row, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input aria-label={t('billing.account.plan_name')} placeholder={t('billing.account.plan_placeholder')}
                  value={row.plan} onChange={(e) => setPlan(index, { plan: e.target.value })} aria-invalid={!!errors.included} />
                <Input aria-label={t('billing.account.plan_credits')} placeholder={t('billing.account.credits_placeholder')}
                  inputMode="numeric" className="w-32" value={row.credits}
                  onChange={(e) => setPlan(index, { credits: e.target.value })} aria-invalid={!!errors.included} />
                <Button type="button" variant="ghost" size="icon-sm" aria-label={t('billing.account.remove_plan')}
                  onClick={() => setPlans((rows) => rows.filter((_, i) => i !== index))}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={() => setPlans((rows) => [...rows, { plan: '', credits: '' }])}>
              <Plus className="mr-2 h-4 w-4" />
              {t('billing.account.add_plan')}
            </Button>
            <FieldError id="billing-included-error">{errors.included}</FieldError>
          </fieldset>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="billing-fee-fixed">{t('billing.account.fee_fixed', { currency })}</Label>
              <Input id="billing-fee-fixed" aria-label={t('billing.account.fee_fixed', { currency })} inputMode="decimal" value={feeFixed} onChange={(e) => setFeeFixed(e.target.value)}
                aria-invalid={!!errors.fee_fixed} aria-describedby={errors.fee_fixed ? 'billing-fee-fixed-error' : undefined} />
              <FieldError id="billing-fee-fixed-error">{errors.fee_fixed}</FieldError>
            </div>
            <div className="space-y-1">
              <Label htmlFor="billing-fee-percent">{t('billing.account.fee_percent')}</Label>
              <Input id="billing-fee-percent" aria-label={t('billing.account.fee_percent')} inputMode="decimal" value={feePercent} onChange={(e) => setFeePercent(e.target.value)}
                aria-invalid={!!errors.fee_percent} aria-describedby={errors.fee_percent ? 'billing-fee-percent-error' : undefined} />
              <FieldError id="billing-fee-percent-error">{errors.fee_percent}</FieldError>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{t('billing.account.fee_help')}</p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('billing.common.cancel')}</Button>
            <Button type="submit" disabled={saving}>{account ? t('billing.common.save') : t('billing.account.create')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
